import { Fragment, StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { descriptions, performance as performanceNotes } from "./workload-notes.json";

export type Result = {
  state: "compared" | "not-compatible" | "did-not-finish";
  reason?: string;
  estimator?: "median" | "mean";
  uncertainty?: {
    method: string;
    level: number;
    ratioInterval: [number, number];
    candidateIntervalNs: [number, number];
    comparatorIntervalNs: [number, number];
    forkMeanRangeNs: { candidate: [number, number] | null; comparator: [number, number] | null };
  };
  candidateNs?: number;
  comparatorNs?: number;
  ratio?: number;
  minimumRatio?: number;
  maximumRatio?: number;
  deltaNs?: number;
  deltaNsPerByte?: number | null;
  warnings: string[];
  hosts: Array<{
    instanceId?: string;
    instanceType?: string;
    allocatedVcpus?: number;
    replica?: number;
    candidate: { medianNs?: number; meanNs?: number; epochMeansNs?: number[]; state: string; reason?: string };
    comparator: { medianNs?: number; meanNs?: number; epochMeansNs?: number[]; state: string; reason?: string };
  }>;
};
export type Row = {
  id: string;
  caseId: string;
  name: string;
  operation: string;
  model: string;
  population: string;
  family: string;
  language: string;
  platform: string;
  memoryMode: string;
  inputBytes: number | null;
  source: string;
  comparator?: string | null;
  mapping?: { status: string; reason: string; patternPreview: string; patternBytes: number };
  result: Result;
  originalResult?: Result;
  originalResultLabel?: string;
  measurementSource?: Record<string, unknown>;
  hardware?: { instanceType: string; allocatedVcpus: number };
  measurementWarnings?: string[];
  workContract?: string;
  workload?: { pattern?: string; description?: string; performanceContext?: string; inputDescription?: string; flags?: string[]; inputs?: Array<{ text: string; bytes: number }>; columnRows?: number };
  // Warnings for this specialized-pattern variant's gain over each earlier variant, keyed by that variant's row id.
  variantGainWarnings?: Record<string, string[]>;
  patternSummary?: { bytes: number; sourceUrl?: string; fullPatternAvailable: boolean };
};
export type ReportData = {
  schemaVersion: 2;
  sources: { currentLabel: string; currentCandidate: string; engineTree: string; jdk: string; releaseVersion?: string };
  platforms: Record<string, string>;
  memoryModes: Record<string, string>;
  languages: Record<string, { label: string; comparator: string }>;
  rows: Row[];
  methodology: string[];
  provenance: Record<string, unknown>;
  publication?: { status: "preliminary"; note: string; sourcePolicy: "mixed-development-revisions" | "candidate-build" };
  fullResultsUrl?: string;
  selection?: Selection;
};

type Sort = { key: string; descending: boolean } | null;
type SortValue = string | number | null | undefined;
type Selection = { language: string; cpu: string; memory: string };
// The 1.0 release also measured R8g; later campaigns measure only R9g and R8i.
const cpuOrders = [["r9g", "r8g", "r8i"], ["r9g", "r8i"], ["c9g", "c8g", "c8i"]];
export function cpuOrder(data: Pick<ReportData, "platforms">): string[] {
  return cpuOrders.find(order => order.every(cpu => Object.hasOwn(data?.platforms || {}, cpu))) || [];
}
// The 1.0 release measured three regex languages; later campaigns add Rust.
const languageOrders = [["re2", "java", "trino", "rust", "like"], ["re2", "java", "trino", "like"]];
export function languageOrder(data: Pick<ReportData, "languages">): string[] {
  return languageOrders.find(order => order.every(language => Object.hasOwn(data?.languages || {}, language))) || [];
}
const numberFormat = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percentFormat = new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
// Gap magnitudes truncate, so a gap under a cut-off never displays at or above it.
const gapFormat = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 2, roundingMode: "trunc" });

// One nominal rate converts a time gap to instructions on every CPU.
export const INSTRUCTIONS_PER_NS = 10;
// Every significance threshold. Gaps are ns per input byte or per call; below the tie gap a
// difference is under 1 instruction per byte or 20 per call, and its ratio says little about cost.
const tieGap = { byte: .1, call: 2 }, clearGap = { byte: .3, call: 5 };
export const significance = {
  tieGap, clearGap, tieRatio: [.95, 1.05], clearRatio: [.8, 1.25],
  // Labels for gaps under the tie gap and under the clear gap.
  gapLabels: { byte: ["under one instruction per byte", "under three instructions per byte"],
    call: [tieGap.call, clearGap.call].map(ns => `under ${ns * INSTRUCTIONS_PER_NS} instructions per call`) },
} as const;
type Significance = "clear-win" | "modest-win" | "tie" | "modest-loss" | "clear-loss";
type Tier = Significance | "na" | "timeout";
const significanceSymbols: Record<Significance, [string, string]> = {
  "clear-win": ["▲", "Clear win"], "modest-win": ["△", "Modest win"], tie: ["≈", "Tie"],
  "modest-loss": ["▽", "Modest loss"], "clear-loss": ["▼", "Clear loss"],
};

type ComparisonIssue = { label: string; reason: string };

// Classify the recorded call contract, never today's implementation or the ratio.
// Renamed traditional methods identify corrected captures; old names stay historical.
export function comparisonIssue(row: Row): ComparisonIssue | null {
  if (row.source.startsWith("baseline") && row.language === "re2") {
    if (row.operation.startsWith("possibleMatchRange")) return { label: "Different API work", reason: "Regulator internal program range analysis was paired with RE2 public prefix-aware range analysis. Needs a new program-to-program measurement." };
    if (["dotMatch", "asciiMatch", "searchPhoneRe2"].includes(row.operation)) return { label: "Needs remeasurement", reason: "Regulator requested a boolean; native RE2 also extracted captures. Corrected benchmarks request captures on both sides." };
    if (/^search.*Dfa$/.test(row.operation)) return { label: "Needs remeasurement", reason: "Regulator requested a match boundary; native DFA requested only a boolean. Corrected benchmarks request only a boolean on both sides." };
    if (["searchSuccessRe2", "searchSuccess1Re2", "searchAltMatchRe2"].includes(row.operation)) return { label: "Needs remeasurement", reason: "Regulator requested a prefix match; native RE2 requested a full match. Corrected benchmarks request a full match on both sides." };
  }
  if (row.source === "language" && row.operation === "execute") {
    if (row.language === "trino" && ["count-spans", "count-captures", "grep-captures"].includes(row.model) && row.workContract !== "trino-matcher-count-v3") return {
      label: "Different API work", reason: row.model === "count-spans"
        ? "Regulator builds an extracted-value list before summing lengths; Joni sums match boundaries without that list. These timings do not isolate matching speed."
        : "Regulator constructs capture lists and replacement output to count captures; Joni counts capture regions without constructing replacement output. These timings do not isolate matching speed.",
    };
    if (["re2", "java"].includes(row.language) && ["grep", "count"].includes(row.model) && !row.measurementSource && row.workContract !== "public-find-count-v2") return {
      label: "Historical API path", reason: row.model === "grep"
        ? "This capture used matcher boundary discovery, not the current public boolean find call. Retained for reference; needs remeasurement."
        : "This capture used reusable matcher iteration, not the current public count call. Retained for reference; needs remeasurement.",
    };
  }
  if (row.source.startsWith("baseline") && row.language === "trino" && ["extractAll", "split", "replaceLambda"].includes(row.operation) && row.workContract !== "joni-slice-output-v1") return {
    label: "Different output APIs", reason: "Regulator uses Slice lists; the Trino/Joni comparator uses SQL Blocks, including value-copying for extraction and split. These are different integration boundaries, not a pure engine comparison.",
  };
  return null;
}

export function downloadData(data: ReportData) {
  return { ...data, rows: data.rows.map(row => ({ ...row, comparisonAssessment: comparisonIssue(row), reportSection: workloadSection(row), commentary: workloadCommentary(row) })) };
}

export function workloadSection(row: Row): string {
  if (row.operation === "compile" || row.population === "compilation") return "diagnostic-only";
  if (row.language === "like" && row.caseId.endsWith("/optimized")) return likeDfaVariant(row) ? "like" : "diagnostic-only";
  if (["captures/contiguous-letters", "grep/every-line", "wild/dot-star-capture/rust-src-tools", "reported/i787-keywords/opt-ascii"].includes(row.caseId)) return "diagnostic-only";
  if (row.caseId.startsWith("unicode/codepoints/") || row.caseId.startsWith("unicode/overlapping-words/")) return "synthetic-stress";
  if (row.caseId === "reported/i787-keywords/ascii") return "bulk-text";
  if (["imported/leipzig/certain-long-strings-ending-x", "imported/sherlock/repeated-class-negation"].includes(row.caseId)) return "diagnostics-and-stress";
  return row.population;
}

export function summaryRatios(data: ReportData) {
  // Sum logs instead of multiplying ratios, which can overflow or underflow.
  const geometricMean = (rows: Row[]) => rows.length
    ? Math.exp(rows.reduce((sum, row) => sum + Math.log(row.result.ratio!), 0) / rows.length)
    : null;
  return Object.fromEntries(languageOrder(data).map(language => {
    // Host disagreement describes variability, not invalid measurements. Keep
    // those row estimates in this summary; individual rows retain their warnings.
    const rows = data.rows.filter(row => row.language === language && row.platform === cpuOrder(data)[0]
      && row.memoryMode === "native" && row.result.state === "compared" && !comparisonIssue(row)
      && Number.isFinite(row.result.ratio) && row.result.ratio! > 0);
    return [language, {
      everyday: geometricMean(rows.filter(row => language === "like"
        ? workloadSection(row) === "like" && row.operation === "matches" && row.caseId !== "trino-like/ORDERED_DENSE_FALSE"
        : workloadSection(row) === "ordinary-scalar" && ["reusedContains", "reusedCount"].includes(row.operation))),
      textProcessing: language === "like" ? null : geometricMean(rows.filter(row => workloadSection(row) === "bulk-text")),
    }];
  }));
}

export function workloadCommentary(row: Row) {
  const matches = (note: { cases?: string[]; prefixes?: string[] }) =>
    note.cases?.includes(row.caseId) || note.prefixes?.some(prefix => row.caseId.startsWith(prefix));
  return {
    description: descriptions.find(matches)?.text,
    performance: row.result.state === "not-compatible" ? undefined : (row.workload?.performanceContext ?? performanceNotes.find(note => matches(note)
      && (!note.languages || note.languages.includes(row.language))
      && note.operations.includes(row.operation)
      && (!note.qualifiedOnly || row.measurementSource?.cohort != null))?.text) || undefined,
  };
}

export function displayResult(result: Result): Result {
  if (result.state === "compared") return result;
  const completedTime = (side: "candidate" | "comparator") => {
    const hosts = result.hosts;
    if (!hosts.length || hosts.some(host => host[side].state !== "compared" || !Number.isFinite(host[side].medianNs))) return undefined;
    const values = hosts.map(host => host[side].medianNs!).sort((a, b) => a - b);
    const middle = Math.floor(values.length / 2);
    return values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2;
  };
  // Preserve the successful engine's measured host median, never a timeout limit
  // or an estimate. Without both engines there is no numerical comparison.
  return { ...result, candidateNs: completedTime("candidate"), comparatorNs: completedTime("comparator"),
    ratio: undefined, minimumRatio: undefined, maximumRatio: undefined, deltaNs: undefined, deltaNsPerByte: undefined };
}

export function outcomeLabel(result: Result, comparator: string): string {
  if (result.hosts.length > 0 && result.hosts.every(host =>
    host.candidate.state === "not-compatible" && host.comparator.state === "not-compatible")) {
    return "Neither engine is compatible";
  }
  const labels: string[] = [];
  for (const [side, name] of [["candidate", "Regulator"], ["comparator", comparator]] as const) {
    const states = new Set(result.hosts.map(host => host[side].state));
    if (states.has("not-compatible")) labels.push(`${name} not compatible`);
    else if (states.has("did-not-finish")) labels.push(`${name} did not finish`);
  }
  return labels.join("; ") || comparisonText(result);
}

export function sorted<T>(rows: T[], sort: Sort, value: (row: T, key: string) => SortValue): T[] {
  if (!sort) return rows;
  return [...rows].sort((left, right) => {
    const a = value(left, sort.key), b = value(right, sort.key);
    if (a == null) return b == null ? 0 : 1;
    if (b == null) return -1;
    const difference = typeof a === "number" && typeof b === "number"
      ? a - b : String(a).localeCompare(String(b), undefined, { numeric: true });
    return sort.descending ? -difference : difference;
  });
}

export function duration(ns: number | null | undefined, signed = false): string {
  if (ns == null) return "";
  const magnitude = Math.abs(ns);
  const sign = signed ? ns < 0 ? "−" : ns > 0 ? "+" : "" : "";
  const [divisor, unit] = magnitude >= 1e9 ? [1e9, "s"]
    : magnitude >= 1e6 ? [1e6, "ms"] : magnitude >= 1e3 ? [1e3, "µs"] : [1, "ns"];
  return `${sign}${numberFormat.format(magnitude / divisor)} ${unit}`;
}

export function comparisonText(result: Result): string {
  if (result.state === "not-compatible") return "not compatible";
  if (result.state === "did-not-finish") return "did not finish";
  if (intervalIncludesOne(result)) return "no clear difference";
  if (!result.uncertainty && result.warnings?.some(warning => warning.includes("disagree"))) return "no consistent winner";
  if (!result.uncertainty && result.warnings?.length) return "timing variable";
  const ratio = result.ratio!;
  if (!result.uncertainty && ratio > .98 && ratio < 1.02) return "< 2% difference";
  const factor = ratio < 1 ? 1 / ratio : ratio;
  const change = ratio < 1 ? "faster" : "slower";
  return factor < 1.1 ? `${percentFormat.format((factor - 1) * 100)}% ${change}`
    : `${numberFormat.format(factor)}× ${change}`;
}

// The site's test for no clear difference.
function intervalIncludesOne(result: Result): boolean {
  const interval = result.uncertainty?.ratioInterval;
  return !!interval && interval[0] <= 1 && 1 <= interval[1];
}

const bytesBasisSections = ["bulk-text", "literal-probe", "synthetic-stress", "diagnostics-and-stress"];
type Gap = { basis: "byte" | "call"; ns: number };

// Regulator's signed time difference: per input byte in the text-processing, literal-probe and
// stress sections, otherwise per call, as also when a row has no per-byte delta.
function comparisonGap(row: Row): Gap | null {
  const { state, deltaNs, deltaNsPerByte } = row.result;
  if (state !== "compared" || deltaNs == null) return null;
  return bytesBasisSections.includes(workloadSection(row)) && deltaNsPerByte != null ? { basis: "byte", ns: deltaNsPerByte } : { basis: "call", ns: deltaNs };
}

function gapSize(gap: Gap): string {
  return `${gapFormat.format(Math.abs(gap.ns))} ns/${gap.basis}`;
}

// Single-use timings include compilation, so only steady-state losses get a gap.
export function lossGap(row: Row): (Gap & { instructions: number; label: string | null }) | null {
  const gap = comparisonGap(row);
  if (!gap || !(row.result.ratio! > 1) || comparisonIssue(row) || row.operation.startsWith("singleUse")) return null;
  const [tieLabel, modestLabel] = significance.gapLabels[gap.basis];
  return { ...gap, instructions: gap.ns * INSTRUCTIONS_PER_NS,
    label: gap.ns < significance.tieGap[gap.basis] ? tieLabel : gap.ns < significance.clearGap[gap.basis] ? modestLabel : null };
}

// Each tie condition that holds, in words: the ratio interval or the host range includes 1.0, the
// ratio is near 1, or the gap is small.
function tieConditions(result: Result, gap: Gap | null): string[] {
  const { tieGap, tieRatio: [low, high] } = significance;
  const range = ([from, to]: [number, number]) => `${from.toFixed(3)}–${to.toFixed(3)}×`;
  const { minimumRatio, maximumRatio } = result;
  return [
    intervalIncludesOne(result) && `ratio interval ${range(result.uncertainty!.ratioInterval)} includes 1.0`,
    minimumRatio != null && maximumRatio != null && minimumRatio <= 1 && 1 <= maximumRatio && `host ratio range ${range([minimumRatio, maximumRatio])} includes 1.0`,
    low <= result.ratio! && result.ratio! <= high && `ratio ${result.ratio!.toFixed(3)}× is within ${low}–${high}×`,
    gap && Math.abs(gap.ns) < tieGap[gap.basis] && `gap ${gapSize(gap)} is under ${tieGap[gap.basis]} ns/${gap.basis}`,
  ].filter((condition): condition is string => typeof condition === "string");
}

// Any one tie condition makes a tie. Clear needs both a large gap and a large ratio. Without a gap,
// as for gains between Regulator variants, the ratio and its interval decide. Results that were not
// compared, and timing warnings without an interval, get no tier.
export function significanceTier(result: Result, gap: Gap | null): Tier {
  if (result.state !== "compared") return result.state === "not-compatible" ? "na" : "timeout";
  if (!result.uncertainty && result.warnings.length > 0) return "na";
  if (tieConditions(result, gap).length > 0) return "tie";
  const ratio = result.ratio!;
  const [clearLow, clearHigh] = significance.clearRatio;
  const clear = (!gap || Math.abs(gap.ns) >= significance.clearGap[gap.basis]) && (ratio <= clearLow || ratio >= clearHigh);
  return `${clear ? "clear" : "modest"}-${ratio < 1 ? "win" : "loss"}`;
}

export function rowSignificance(row: Row): Tier {
  return significanceTier(row.result, comparisonGap(row));
}

function TierSymbol({ tier }: { tier: Tier }) {
  if (tier === "na" || tier === "timeout") return null;
  const [symbol, name] = significanceSymbols[tier];
  return <span className="tier-symbol" role="img" aria-label={name}>{symbol}</span>;
}

// A tie names the conditions that made it one.
function tierSentence(result: Result, gap: Gap | null, tier: Tier): string[] {
  if (tier === "na" || tier === "timeout") return [];
  if (tier === "tie") return [`Tie: ${tieConditions(result, gap).join("; ")}.`];
  return [`${significanceSymbols[tier][1]}.`];
}

function evidence(row: Row, tier: Tier): string {
  const { result } = row;
  if (result.state !== "compared") return result.reason || result.state;
  const gap = lossGap(row);
  return [
    ...tierSentence(result, comparisonGap(row), tier),
    `Regulator ${duration(result.candidateNs)}; comparator ${duration(result.comparatorNs)}.`,
    `${result.estimator === "mean" ? "Ratio of mean costs" : "Same-host ratio median"} ${result.ratio!.toFixed(4)}×; host ratio range ${result.minimumRatio!.toFixed(4)}–${result.maximumRatio!.toFixed(4)}×.`,
    ...(result.uncertainty ? [`Approximate 95% ratio interval ${result.uncertainty.ratioInterval.map(value => value.toFixed(4)).join(" to ")}×.`] : []),
    `${result.hosts.length} independent hosts; the observed range is not a confidence interval.`,
    ...(gap ? [`Gap +${gapSize(gap)} ≈ ${gapFormat.format(gap.instructions)} instructions per ${gap.basis} at ${INSTRUCTIONS_PER_NS} instructions/ns.`] : []),
    ...result.warnings,
  ].join(" ");
}

function Ratio({ row, comparator, children }: { row?: Row; comparator: string; children?: React.ReactNode }) {
  if (!row) return null;
  const tier = rowSignificance(row);
  return <span className={`scan-result ${tier}`} title={evidence(row, tier)}>
    <TierSymbol tier={tier} />{row.result.state === "compared" ? comparisonText(row.result) : outcomeLabel(row.result, comparator)}{children}
  </span>;
}

function GapCell({ row }: { row?: Row }) {
  const gap = row && lossGap(row);
  return <td className="measurement gap">{gap && <>{gapFormat.format(gap.instructions)} instr/{gap.basis === "byte" ? "B" : "call"}
    {gap.label && <small className="gap-label">{gap.label}</small>}</>}</td>;
}

type TimingSide = "candidate" | "comparator";

export function timingUncertainty(result: Result, side: TimingSide): number | null {
  const cost = side === "candidate" ? result.candidateNs : result.comparatorNs;
  if (result.state !== "compared" || !result.uncertainty || !cost || cost <= 0) return null;
  const [low, high] = result.uncertainty[side === "candidate" ? "candidateIntervalNs" : "comparatorIntervalNs"];
  return (high - low) / (2 * cost);
}

function QualityMarker({ symbol, target, label }: { symbol: string; target: string; label: string }) {
  return <sup><button type="button" className="quality-marker" title={label} aria-label={label} onClick={() => {
    const note = document.getElementById(target);
    note?.scrollIntoView({ block: "center" });
    note?.focus({ preventScroll: true });
  }}>{symbol}</button></sup>;
}

function TimingFlags({ row, side, comparator }: { row: Row; side: TimingSide; comparator: string }) {
  const uncertainty = timingUncertainty(row.result, side);
  const engine = side === "candidate" ? "Regulator" : comparator;
  return uncertainty !== null && uncertainty > .05 ? <QualityMarker symbol="*" target="quality-high"
    label={`${engine} timing uncertainty: ${percentFormat.format(100 * uncertainty)}%. Exceeds 5%.`} /> : null;
}

function LifecycleFlags({ row, comparator }: { row?: Row; comparator: string }) {
  if (!row) return null;
  const sides = (["candidate", "comparator"] as const).filter(side => (timingUncertainty(row.result, side) ?? 0) > .05);
  return sides.length > 0 ? <QualityMarker symbol="*" target="quality-high" label={sides.map(side =>
    `${side === "candidate" ? "Regulator" : comparator} timing uncertainty: ${percentFormat.format(100 * timingUncertainty(row.result, side)!)}%`).join("; ")} /> : null;
}

export function displayedMeasurements(rows: Row[]): Row[] {
  const eligible = rows.filter(row => workloadSection(row) !== "diagnostic-only" && !comparisonIssue(row));
  const displayed = eligible.filter(row => (row.population === "ordinary-scalar" && row.operation.startsWith("reused"))
    || row.population === "trino-operations"
    || ["specialized-patterns", "bulk-text", "literal-probe", "diagnostics-and-stress", "synthetic-stress"].includes(workloadSection(row)));
  for (const pair of lifecycleRows(eligible)) {
    if (pair.single) displayed.push(pair.single);
    displayed.push(pair.reused);
  }
  return [...new Map(displayed.map(row => [row.id, row])).values()];
}

export function qualityDistribution(rows: Row[], side: TimingSide) {
  // Specialized-pattern variants share one comparator measurement, carried by the specialized-engine row.
  const measured = side === "comparator" ? rows.filter(row => row.population !== "specialized-patterns" || specializedVariantOf(row).key === "specialized") : rows;
  const values = measured.map(row => timingUncertainty(row.result, side)).filter((value): value is number => value !== null).sort((a, b) => a - b);
  if (!values.length) return null;
  const quantile = (fraction: number) => {
    const index = (values.length - 1) * fraction;
    const low = Math.floor(index);
    return values[low] + (values[Math.min(low + 1, values.length - 1)] - values[low]) * (index - low);
  };
  return { count: values.length, median: quantile(.5), p90: quantile(.9), aboveFive: values.filter(value => value > .05).length };
}

function MeasurementQuality({ rows, comparator, platform, memory, cpu, releaseVersion }: { rows: Row[]; comparator: string; platform: string; memory: string; cpu: string; releaseVersion?: string }) {
  const candidate = qualityDistribution(rows, "candidate");
  const control = qualityDistribution(rows, "comparator");
  if (!candidate || !control) return null;
  const percent = (value: number) => `${numberFormat.format(value * 100)}%`;
  return <details className="measurement-quality" aria-label="Measurement quality">
    <summary>Median timing uncertainty: Regulator {percent(candidate.median)} · {comparator} {percent(control.median)} <span className="quality-details-label">Details</span></summary>
    <div className="quality-details">
    <p>{platform} · {memory}</p>
    <p>{candidate.count} distinct numeric comparisons on this page, including the expandable stress and diagnostic groups. Measurements repeated in multiple tables count once.</p>
    <div className="table-scroll"><table className="quality-summary"><thead><tr><th>Timing uncertainty</th><th>Regulator</th><th>{comparator}</th></tr></thead>
      <tbody><tr><th>Median</th><td>{percent(candidate.median)}</td><td>{percent(control.median)}</td></tr>
      <tr><th>90th percentile</th><td>{percent(candidate.p90)}</td><td>{percent(control.p90)}</td></tr>
      <tr><th>Above 5%</th><td>{candidate.aboveFive} of {candidate.count}</td><td>{control.aboveFive} of {control.count}</td></tr></tbody></table></div>
    <p>Percentages are half the approximate 95% interval width divided by mean execution time. For 90% of measurements, uncertainty is at or below the 90th-percentile value.</p>
    <p>Java timings use JMH. Costs average iterations, then independent processes, then hosts equally. Individual JVM executions can vary more than the uncertainty in their average. Expand a flagged workload for its measurement note. Full intervals and process measurements remain in the results download.</p>
    <details className="quality-methodology"><summary>Measurement methodology</summary>
      <p>Intervals resample paired hosts and whole processes. Native comparators use Google Benchmark; repetitions within one native process remain together. “No clear difference” means the ratio interval includes parity, regardless of the 5% annotation threshold.</p>
      {releaseVersion === "1.0" && <p>The measurement follow-up replaced affected collections with isolated JMH forks, longer qualified timing windows and separate allocation profiling. Other results retain their original samples. The Regulator 1.0 implementation did not change.</p>}
      <p>Intervals describe the sampled hosts and processes. They do not guarantee repeatability across future collections.{releaseVersion === "1.0" && " The full assessment also covers independent-run differences and distinct process timing levels in diagnostic workloads."}</p>
      {releaseVersion === "1.0" && <p><a href="./measurement-quality.txt" target="_blank" rel="noreferrer">Full measurement-quality assessment</a></p>}
    </details>
    {releaseVersion === "1.0" && <PlatformMeasurements cpu={cpu} />}
    </div>
  </details>;
}

function PlatformMeasurements({ cpu }: { cpu: string }) {
  // Fixed 256 KiB C++ control observations from the 1.0 measurement follow-up.
  const controls = ({ r8g: ["0.06%", "0.03%"], r9g: ["0.44%", "0.24%"], r8i: ["1.03%", "0.41%"] } as Record<string, string[]>)[cpu];
  return <>
    {controls && <details className="quality-methodology"><summary>Platform measurements: native controls</summary>
      <p>Across 270 instances of this CPU family, fixed 256 KiB native RE2 controls had host-to-host coefficients of variation of {controls[0]} for Easy0 and {controls[1]} for Easy2. These are standard deviations relative to mean cost, not confidence intervals or minimum-to-maximum ranges.</p>
      <p>These controls describe those workloads under the follow-up collection conditions, not inherent machine variability. Large native working sets can vary substantially more.</p>
    </details>}
  </>;
}

function MeasurementNotes({ rows }: { rows: Row[] }) {
  const high = rows.some(row => (["candidate", "comparator"] as const).some(side => (timingUncertainty(row.result, side) ?? 0) > .05));
  const compared = rows.some(row => row.result.state === "compared");
  if (!high && !compared) return null;
  const { tieGap, clearGap, tieRatio, clearRatio, gapLabels } = significance;
  const symbol = (tier: Significance) => <TierSymbol tier={tier} />;
  return <section className="measurement-notes" aria-label="Measurement notes"><h2>Measurement notes</h2>
    {high && <p id="quality-high" tabIndex={-1}><strong>* Higher uncertainty.</strong> The approximate 95% interval half-width exceeds 5% of that engine’s mean time. In lifecycle tables, the marker applies to either engine; expand the row to see which. This is an uncertainty annotation, not a failed measurement.</p>}
    {compared && <><p><strong>Reading a comparison.</strong> Each comparison is judged by its ratio and by Regulator’s absolute gap: per input byte for text processing, literal-probe and stress rows, and per call for all others.
      It is a tie ({symbol("tie")}, gray) when any one of these holds: the gap is under {tieGap.byte} ns per byte or {tieGap.call} ns per call; the ratio is between {tieRatio[0]}× and {tieRatio[1]}×; the range of per-host ratios includes 1.0; or the 95% ratio interval includes 1.0.
      A clear win or loss ({symbol("clear-win")} or {symbol("clear-loss")}, full green or red) needs both a gap of at least {clearGap.byte} ns per byte or {clearGap.call} ns per call and a ratio of at most {clearRatio[0]}× or at least {clearRatio[1]}×.
      Every other win or loss is modest ({symbol("modest-win")} or {symbol("modest-loss")}, pale green or red). Gains between Regulator variants have no gap or host range, so their ratio and interval decide. A result’s tooltip names the tie conditions that held.
      <span className="significance-legend">{(Object.keys(significanceSymbols) as Significance[]).map(tier => <span key={tier} className={`scan-result ${tier}`}>{symbol(tier)}{significanceSymbols[tier][1].toLowerCase()}</span>)}</span></p>
    <p><strong>Gap (instructions).</strong> Each comparison where Regulator is slower also gives its extra time in instructions, at a nominal {INSTRUCTIONS_PER_NS} instructions per ns on every CPU. Lifecycle tables give it for multi-use only, because single-use times include compilation.
      Per byte, a gap under {tieGap.byte} ns reads “{gapLabels.byte[0]}” and one under {clearGap.byte} ns “{gapLabels.byte[1]}”; per call, a gap under {tieGap.call} ns reads “{gapLabels.call[0]}” and one under {clearGap.call} ns “{gapLabels.call[1]}”.</p></>}
  </section>;
}

function Header({ label, column, sort, setSort, title }: {
  label: string; column: string; sort: Sort; setSort: (sort: Sort) => void; title?: string;
}) {
  const active = sort?.key === column;
  return <th aria-sort={active ? sort.descending ? "descending" : "ascending" : "none"}>
    <button type="button" className="sort-button" title={title} onClick={() => setSort({ key: column, descending: active ? !sort.descending : false })}>
      {label}<span aria-hidden="true">{active ? sort.descending ? "↓" : "↑" : "↕"}</span>
    </button>
  </th>;
}

function operationDescription(row: Row): string {
  if (row.workload?.columnRows != null) return "Count the rows of one column that match a warmed LIKE pattern.";
  const operation = row.operation === "execute" ? row.model : row.operation;
  // Only Trino's API has an extractAll call that returns a Slice list; other languages' rows loop over a matcher.
  if (operation === "extractAll" && row.language !== "trino") return "Extract the selected capture from every match.";
  return ({
    count: "Count all non-overlapping matches in the input.",
    "count-spans": "Find all matches and sum their matched lengths.",
    "count-captures": "Find all matches and count participating capture groups, including the whole match.",
    grep: "Test each line for a match and count matching lines.",
    "grep-captures": "Find the first match on each line and count its participating capture groups.",
    compile: "Construct the compiled pattern without executing it.",
    reusedContains: "Test one input for a match using a warmed compiled pattern.",
    reusedCount: "Count matches in one input using a warmed compiled pattern.",
    singleUseContains: "Compile the pattern, then test one input for a match.",
    singleUseCount: "Compile the pattern, then count matches in one input.",
    singleUse: "Compile the LIKE pattern, then match one input.",
    matches: "Match one complete input using a warmed LIKE pattern.",
    contains: "Test one input for a match.",
    extract: "Extract the selected capture from the first match.",
    extractAll: "Extract the selected capture from every match into a Slice list.",
    split: "Split the input at matches and return a Slice list.",
    replace: "Replace matches and construct the resulting Slice.",
    replaceLambda: "Replace matches using a capture-based function and construct the resulting Slice.",
  } as Record<string, string>)[operation] || operationName(operation);
}

// Variants are labelled rows measured beside the summary row, such as each specialized-pattern variant.
type EvidenceProps = { row: Row; reused?: Row; variants?: Array<{ label: string; row: Row }>; comparator: string; hideOperation?: boolean; missingSingle?: boolean };

function Name(props: EvidenceProps) {
  const [open, setOpen] = useState(false);
  const { row, hideOperation = false } = props;
  return <details className="row-evidence" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>{row.name.replace(/^everyday\//, "")}{!hideOperation && <small>{row.operation === "execute" ? row.model : operationName(row.operation)}</small>}</summary>
    {open && <RowEvidence {...props} />}
  </details>;
}

export function RowEvidence({ row, reused, variants, comparator, missingSingle = false }: EvidenceProps) {
  const reason = (result: Result) => result.reason?.replace(/\bcandidate:/g, "Regulator:").replace(/\bcomparator:/g, `${comparator}:`);
  const commentary = workloadCommentary(reused ?? row);
  const singleUseCommentary = reused ? workloadCommentary(row).performance : undefined;
  const phases: Array<{ label?: string; variation: string; row: Row }> = variants?.map(variant => ({ ...variant, variation: `${variant.label} measurement variation` }))
    ?? (reused ? [{ label: "Single use", variation: "Single-use measurement variation", row }, { label: "Multi-use", variation: "Multi-use measurement variation", row: reused }]
      : [{ variation: "Measurement variation", row }]);
  return <div className="row-evidence-body">
      {missingSingle && <p>Single-use timings were not collected with Trino's DFA enabled. Single-use comparisons are left blank.</p>}
      {comparisonIssue(row) && <p>{comparisonIssue(row)!.reason}</p>}
      {phases.map(phase => phase.row.result.reason && <p key={phase.variation}>{phase.label && `${phase.label}: `}{reason(phase.row.result)}</p>)}
      {(commentary.description || row.workload?.description) && <><h4>What this tests</h4><p>{commentary.description ?? row.workload?.description}</p></>}
      {singleUseCommentary && singleUseCommentary !== commentary.performance && <><h4>Single-use performance context</h4><p>{singleUseCommentary}</p></>}
      {commentary.performance && <><h4>{reused && singleUseCommentary !== commentary.performance ? "Multi-use performance context" : "Performance context"}</h4><p>{commentary.performance}</p></>}
      {(row.workload?.pattern !== undefined || row.mapping?.patternPreview !== undefined) && <><h4>Pattern</h4><pre>{row.workload?.pattern ?? row.mapping?.patternPreview}</pre></>}
      {row.patternSummary && <p>This pattern contains {numberFormat.format(row.patternSummary.bytes / 1000)} KB of regex text. Only a short preview is shown.
        {row.patternSummary.sourceUrl && <> See the <a href={row.patternSummary.sourceUrl}>benchmark definition and pattern source</a>.</>}
        {row.patternSummary.fullPatternAvailable && <> The full pattern is also included in the full results download.</>}</p>}
      {row.workload?.flags?.length ? <p>Flags: {row.workload.flags.join(", ")}</p> : null}
      <p>{operationDescription(row)}{reused && ` ${operationDescription(reused)}`}</p>
      {row.inputBytes != null && <p>{new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(row.inputBytes)} input bytes per operation{!Number.isInteger(row.inputBytes) && " on average"}.</p>}
      {row.workload?.inputDescription && <p>{row.workload.inputDescription}</p>}
      {row.workload?.inputs && <><h4>Rotating inputs</h4>{row.workload.inputs.map((input, index) => <pre key={index}>{input.text}</pre>)}</>}
      {phases.map(phase => <MeasurementAnnotations key={phase.variation} row={phase.row} comparator={comparator} phase={phase.label} />)}
      {phases.map(phase => !phase.row.result.uncertainty && phase.row.measurementWarnings?.length
        ? <Fragment key={phase.variation}><h4>{phase.variation}</h4><p>{phase.row.measurementWarnings.join(". ")}.</p></Fragment> : null)}
      {row.mapping && !row.result.reason && !["identical", "direct"].includes(row.mapping.status) && <p>{row.mapping.reason}</p>}
    </div>;
}

function MeasurementAnnotations({ row, comparator, phase }: { row: Row; comparator: string; phase?: string }) {
  const notes: string[] = [];
  for (const side of ["candidate", "comparator"] as const) {
    const uncertainty = timingUncertainty(row.result, side);
    if (uncertainty !== null && uncertainty > .05) {
      notes.push(`* ${side === "candidate" ? "Regulator" : comparator} timing uncertainty: ${percentFormat.format(100 * uncertainty)}%, above the 5% annotation threshold.`);
    }
  }
  if (!notes.length) return null;
  return <><h4>{phase ? `${phase} measurement notes` : "Measurement notes"}</h4>
    {notes.map(note => <p key={note}>{note}</p>)}</>;
}

function operationName(operation: string): string {
  return ({ reusedContains: "contains", reusedCount: "count", singleUseContains: "single-use contains", singleUseCount: "single-use count", compile: "construction", singleUse: "single use", matches: "reused match" } as Record<string, string>)[operation] || operation;
}

const gapTitle = `Regulator's extra time where it is slower, in instructions at a nominal ${INSTRUCTIONS_PER_NS} instructions/ns`;

function ResultsTable({ rows, comparator }: { rows: Row[]; comparator: string }) {
  const [sort, setSort] = useState<Sort>(null);
  const bytes = rows.some(row => !comparisonIssue(row) && row.result.deltaNsPerByte != null);
  const numeric = rows.some(row => row.result.candidateNs != null || row.result.comparatorNs != null);
  const comparable = rows.some(row => !comparisonIssue(row));
  const ordered = sorted(rows, sort, (row, key) => key === "name" ? row.name : key === "gap" ? lossGap(row)?.instructions : comparisonIssue(row) && ["ratio", "deltaNs", "deltaNsPerByte"].includes(key) ? null : (row.result as unknown as Record<string, SortValue>)[key]);
  return <div className="table-scroll"><table className="measurement-table">
    <thead><tr>
      <Header label="Pattern / workload" column="name" sort={sort} setSort={setSort} />
      {numeric && <><Header label="Regulator" column="candidateNs" sort={sort} setSort={setSort} />
        <Header label={comparator} column="comparatorNs" sort={sort} setSort={setSort} /></>}
      <Header label={`vs ${comparator}`} column="ratio" sort={sort} setSort={setSort} />
      {numeric && comparable && <Header label="Δ / operation" column="deltaNs" sort={sort} setSort={setSort} />}
      {bytes && <Header label="Δ ns / byte" column="deltaNsPerByte" sort={sort} setSort={setSort} />}
      {numeric && comparable && <Header label="Gap (instructions)" column="gap" sort={sort} setSort={setSort} title={gapTitle} />}
    </tr></thead>
    <tbody>{ordered.map(row => <tr key={row.id} className={`comparison-row ${comparisonIssue(row) ? "na" : rowSignificance(row)}`}>
      <td><Name row={row} comparator={comparator} /></td>
      {numeric && <><td className="measurement">{duration(row.result.candidateNs)}<TimingFlags row={row} side="candidate" comparator={comparator} /></td><td className="measurement">{duration(row.result.comparatorNs)}<TimingFlags row={row} side="comparator" comparator={comparator} /></td></>}
      <td className="measurement">{comparisonIssue(row) ? <span>{comparisonIssue(row)!.label}</span> : <Ratio row={row} comparator={comparator} />}</td>
      {numeric && comparable && <td className="measurement delta">{!comparisonIssue(row) && duration(row.result.deltaNs, true)}</td>}
      {bytes && <td className="measurement delta" title={row.result.deltaNsPerByte != null ? `${row.result.deltaNsPerByte} ns per input byte` : ""}>
        {!comparisonIssue(row) && row.result.deltaNsPerByte != null && `${row.result.deltaNsPerByte < 0 ? "−" : row.result.deltaNsPerByte > 0 ? "+" : ""}${numberFormat.format(Math.abs(row.result.deltaNsPerByte))}`}
      </td>}
      {numeric && comparable && <GapCell row={row} />}
    </tr>)}</tbody>
  </table></div>;
}

// Only these collected LIKE workloads select Trino's DFA when enabled; LIKE_DFA_PATTERNS in
// scripts/publication_data.py keeps the same set public.
function likeDfaVariant(row: Row): boolean {
  return row.language === "like" && /^trino-like\/ANY_(ASCII|MULTIBYTE)(\/optimized)?$/.test(row.caseId);
}

type Lifecycle = { id: string; name: string; single?: Row; reused: Row; breakEven: number | null; reason: string };

// Default reading order follows workload purpose, never measured performance.
const likeWorkloadOrder = [
  "PREFIX_LARGE", "SUFFIX_LARGE", "EXACT_MATCH", "CONTAINS_LATE", "CONTAINS_ABSENT",
  "ORDERED_LATE", "MIXED_LATE", "MIXED_ABSENT", "WILDCARD_CHAIN", "ANY_ASCII", "ANY_MULTIBYTE",
];

export function lifecycleRows(rows: Row[]): Lifecycle[] {
  const byKey = new Map(rows.map(row => [`${row.caseId}/${row.operation}`, row]));
  const lifecycle: Lifecycle[] = rows.filter(row => ["singleUseContains", "singleUseCount", "singleUse"].includes(row.operation)).map(single => {
    const reusedName = single.operation === "singleUse" ? "matches" : single.operation.replace("singleUse", "reused");
    const reused = byKey.get(`${single.caseId}/${reusedName}`);
    if (!reused) throw new Error(`Missing reused lifecycle partner for ${single.id}`);
    const bothCompared = single.result.state === "compared" && reused.result.state === "compared";
    const unresolved = comparisonIssue(single) || comparisonIssue(reused)
      || (single.result.uncertainty ? intervalIncludesOne(single.result) : single.result.warnings.length > 0)
      || (reused.result.uncertainty ? intervalIncludesOne(reused.result) : reused.result.warnings.length > 0);
    const deficit = single.result.deltaNs!;
    const saving = -reused.result.deltaNs!;
    // One single-use operation already includes the first execution. Each
    // subsequent use adds the measured reused-operation difference.
    const breakEven = bothCompared && !unresolved ? deficit <= 0 ? 1 : saving > 0 ? 1 + Math.ceil(deficit / saving) : null : null;
    const reason = !bothCompared ? "No comparison for one lifecycle phase" : unresolved ? "Timing varies; no reliable crossover estimate"
      : deficit <= 0 ? saving < 0 ? "Ahead on first use, but each additional use reduces that lead and can reverse it" : "Ahead from first use"
      : saving <= 0 ? "No measured crossover" : "Estimated from independently measured single-use and reused operations";
    const suffix = single.language !== "like" ? ` · ${operationName(reused.operation)}`
      : likeDfaVariant(single) ? ` · DFA ${single.caseId.endsWith("/optimized") ? "enabled" : "disabled"}` : "";
    const name = ({
      "trino-like/SUFFIX_LARGE": "suffix, 6 bytes · 32 KiB input",
      "trino-like/PREFIX_LARGE": "prefix, 6 bytes · 32 KiB input",
      "trino-like/ORDERED_DENSE_FALSE": "ordered dense false · stress test",
    } as Record<string, string>)[single.caseId] ?? single.name.replace(/^everyday\//, "");
    return { id: single.id, name: name + suffix, single, reused, breakEven, reason };
  });
  for (const reused of rows.filter(row => likeDfaVariant(row) && row.caseId.endsWith("/optimized") && row.operation === "matches")) {
    if (byKey.has(`${reused.caseId}/singleUse`)) continue;
    const disabledIndex = lifecycle.findIndex(row => row.reused.caseId === reused.caseId.replace(/\/optimized$/, ""));
    const name = disabledIndex >= 0 ? lifecycle[disabledIndex].name.replace(/ · DFA disabled$/, "") : reused.name;
    const enabled = { id: reused.id, name: `${name} · DFA enabled`, reused, breakEven: null, reason: "Single-use timings not collected" };
    lifecycle.splice(disabledIndex >= 0 ? disabledIndex + 1 : lifecycle.length, 0, enabled);
  }
  if (lifecycle.every(row => row.reused.language === "like")) {
    const rank = (row: Lifecycle) => {
      const scenario = row.reused.caseId.split("/")[1];
      if (scenario === "ORDERED_DENSE_FALSE") return likeWorkloadOrder.length + 1;
      const index = likeWorkloadOrder.indexOf(scenario);
      return index < 0 ? likeWorkloadOrder.length : index;
    };
    lifecycle.sort((left, right) => rank(left) - rank(right)
      || Number(left.reused.caseId.endsWith("/optimized")) - Number(right.reused.caseId.endsWith("/optimized")));
  }
  return lifecycle;
}

function LifecycleTable({ rows, like, comparator }: { rows: Row[]; like: boolean; comparator: string }) {
  const [sort, setSort] = useState<Sort>(null);
  const lifecycle = lifecycleRows(rows);
  const bytes = lifecycle.some(row => row.reused.result.deltaNsPerByte != null);
  const ordered = sorted(lifecycle, sort, (row, key) => {
    if (key === "name") return row.name;
    if (key === "breakEven") return row.breakEven;
    if (key === "reusedBytes") return row.reused.result.deltaNsPerByte;
    if (key === "reusedGap") return lossGap(row.reused)?.instructions;
    return key === "single" ? row.single?.result.ratio : key === "singleDelta" ? row.single?.result.deltaNs
      : key === "reused" ? row.reused.result.ratio : row.reused.result.deltaNs;
  });
  return <div className="table-scroll"><table className="measurement-table lifecycle-table"><thead><tr>
    {[[like ? "Pattern" : "Pattern / operation", "name"], ["Single use", "single"], ["Single-use Δ", "singleDelta"], ["Multi-use", "reused"], ["Per-use Δ", "reusedDelta"], ...(bytes ? [["Δ ns / byte", "reusedBytes"]] : []), ["Gap (instructions)", "reusedGap"], ...(!like ? [["Break-even uses", "breakEven"]] : [])].map(([label, column]) => <Header key={column} label={label} column={column} sort={sort} setSort={setSort} title={column === "reusedBytes" ? "Multi-use time difference per input byte" : column === "reusedGap" ? `Multi-use: ${gapTitle}` : undefined} />)}
  </tr></thead><tbody>{ordered.map(row => <tr key={row.id}>
    <td><Name row={{ ...(row.single ?? row.reused), name: row.name }} reused={row.single ? row.reused : undefined} comparator={comparator} hideOperation={like} missingSingle={!row.single} /></td>
    <td className="measurement"><Ratio row={row.single} comparator={comparator}><LifecycleFlags row={row.single} comparator={comparator} /></Ratio></td>
    <td className={`measurement delta ${row.single ? rowSignificance(row.single) : ""}`}>{duration(row.single?.result.deltaNs, true)}</td>
    <td className="measurement"><Ratio row={row.reused} comparator={comparator}><LifecycleFlags row={row.reused} comparator={comparator} /></Ratio></td>
    <td className={`measurement delta ${rowSignificance(row.reused)}`}>{duration(row.reused.result.deltaNs, true)}</td>
    {bytes && <td className={`measurement delta ${rowSignificance(row.reused)}`} title={row.reused.result.deltaNsPerByte != null ? `${row.reused.result.deltaNsPerByte} ns per input byte` : ""}>
      {row.reused.result.deltaNsPerByte != null && `${row.reused.result.deltaNsPerByte < 0 ? "−" : row.reused.result.deltaNsPerByte > 0 ? "+" : ""}${numberFormat.format(Math.abs(row.reused.result.deltaNsPerByte))}`}
    </td>}
    <GapCell row={row.reused} />
    {!like && <td className="measurement" title={row.reason}>{!row.single ? "" : row.breakEven === 1 ? "first use" : row.breakEven != null ? row.breakEven.toLocaleString() : "not determined"}</td>}
  </tr>)}</tbody></table></div>;
}

type LiteralProbe = { id: string; name: string; distribution: string; standard: Row; tuned: Row; gain: Result };

const literalProbeWorkloads: Record<string, { name: string; description: string }> = {
  "literal-probe/sherlock-exactly": { name: "exactly · Sherlock Holmes text",
    description: "Count the word exactly in the Sherlock Holmes text from the rebar benchmark corpus. The tuned row uses the English text byte-frequency preset." },
  "literal-probe/gharchive-pull-request-event": { name: "\"type\":\"PullRequestEvent\" · GitHub events JSON",
    description: "Count pull request events in the first 8 MiB of one hour of GitHub public events from GH Archive. The tuned row uses the JSON byte-frequency preset." },
  "literal-probe/nginx-mozilla": { name: "Mozilla/5.0 · nginx access log",
    description: "Count Mozilla/5.0 user agents in the second half of the elastic/examples nginx access log. The tuned distribution is learned from up to 256 KiB of whole lines from the first half of each of the Apache and nginx logs, and is built before timing." },
};
const distributionLabels: Record<string, string> = {
  "english-text": "English text preset", json: "JSON preset", "sampled-log-lines": "Learned from sample logs",
};

// Each literal-probe distribution runs on its own hosts beside its own comparator
// run; specialized-pattern variants share each host's comparator run. Either way,
// dividing the two comparator-relative ratios cancels host speed from the
// variant's gain over its baseline. Dividing the far ends of their intervals gives
// a conservative envelope, not a calibrated interval, so the usual
// no-clear-difference rule applies to the gain. Warnings paired host by host, when
// supplied, replace the two variants' warnings against the comparator.
export function variantGain(variant: Result, baseline: Result, pairedWarnings?: string[]): Result {
  const failed = [variant, baseline].find(result => result.state !== "compared");
  if (failed) return { state: failed.state, reason: failed.reason, warnings: [], hosts: [] };
  const [variantLow, variantHigh] = variant.uncertainty?.ratioInterval ?? [];
  const [baselineLow, baselineHigh] = baseline.uncertainty?.ratioInterval ?? [];
  // Only ratio and ratioInterval are read; the other fields only satisfy the Result type.
  const uncertainty = variant.uncertainty && baseline.uncertainty ? {
    method: "conservative-ratio-envelope", level: Math.min(variant.uncertainty.level, baseline.uncertainty.level),
    ratioInterval: [variantLow! / baselineHigh!, variantHigh! / baselineLow!] as [number, number],
    candidateIntervalNs: variant.uncertainty.candidateIntervalNs, comparatorIntervalNs: baseline.uncertainty.candidateIntervalNs,
    forkMeanRangeNs: { candidate: variant.uncertainty.forkMeanRangeNs.candidate, comparator: baseline.uncertainty.forkMeanRangeNs.candidate },
  } : undefined;
  return { state: "compared", ratio: variant.ratio! / baseline.ratio!, ...(uncertainty ? { uncertainty } : {}),
    warnings: pairedWarnings ?? [...new Set([...baseline.warnings, ...variant.warnings])], hosts: [] };
}

function Gain({ result, label }: { result?: Result; label: string }) {
  if (!result) return null;
  const tier = significanceTier(result, null);
  return <span className={`scan-result ${tier}`}
    title={result.state === "compared" ? [...tierSentence(result, null, tier), `${label}: ${result.ratio!.toFixed(4)}×.${result.uncertainty ? ` Conservative interval ${result.uncertainty.ratioInterval.map(value => value.toFixed(4)).join(" to ")}×.` : ""}`].join(" ") : result.reason}><TierSymbol tier={tier} />{comparisonText(result)}</span>;
}

export function literalProbeRows(rows: Row[]): LiteralProbe[] {
  const probes = rows.filter(row => row.population === "literal-probe");
  const byCase = new Map(probes.map(row => [row.caseId, row]));
  for (const tuned of probes.filter(row => row.caseId.endsWith("/tuned"))) {
    if (!byCase.has(tuned.caseId.replace(/\/tuned$/, ""))) throw new Error(`Missing default literal-probe partner for ${tuned.id}`);
  }
  return probes.filter(row => !row.caseId.endsWith("/tuned")).map(standard => {
    const tuned = byCase.get(`${standard.caseId}/tuned`);
    if (!tuned) throw new Error(`Missing tuned literal-probe partner for ${standard.id}`);
    return { id: standard.id, name: literalProbeWorkloads[standard.caseId]?.name ?? standard.name,
      distribution: distributionLabels[tuned.family] ?? tuned.family, standard, tuned, gain: variantGain(tuned.result, standard.result) };
  });
}

function LiteralProbeTable({ rows, comparator }: { rows: Row[]; comparator: string }) {
  const [sort, setSort] = useState<Sort>(null);
  const ordered = sorted(literalProbeRows(rows), sort, (row, key) => ({
    name: row.name, distribution: row.distribution, comparatorNs: row.standard.result.comparatorNs,
    standardNs: row.standard.result.candidateNs, tunedNs: row.tuned.result.candidateNs,
    standard: row.standard.result.ratio, tuned: row.tuned.result.ratio, gain: row.gain.ratio,
    standardGap: lossGap(row.standard)?.instructions, tunedGap: lossGap(row.tuned)?.instructions,
  } as Record<string, SortValue>)[key]);
  return <div className="table-scroll"><table className="measurement-table literal-probe-table"><thead><tr>
    {[["Pattern · corpus", "name"], ["Tuned distribution", "distribution"], [`${comparator} (default run)`, "comparatorNs"], ["Regulator default", "standardNs"], ["Regulator tuned", "tunedNs"],
      [`Default vs ${comparator}`, "standard"], ["Default gap", "standardGap"], [`Tuned vs ${comparator}`, "tuned"], ["Tuned gap", "tunedGap"], ["Tuned vs default", "gain"]].map(([label, column]) =>
      <Header key={column} label={label} column={column} sort={sort} setSort={setSort} title={column === "standardGap" || column === "tunedGap" ? gapTitle : undefined} />)}
  </tr></thead><tbody>{ordered.map(row => <tr key={row.id}>
    <td><Name row={{ ...row.standard, name: row.name, workload: { ...row.standard.workload, description: literalProbeWorkloads[row.standard.caseId]?.description } }} comparator={comparator} hideOperation /></td>
    <td>{row.distribution}</td>
    <td className="measurement">{duration(row.standard.result.comparatorNs)}</td>
    <td className="measurement">{duration(row.standard.result.candidateNs)}</td>
    <td className="measurement">{duration(row.tuned.result.candidateNs)}</td>
    <td className="measurement"><Ratio row={row.standard} comparator={comparator}><LifecycleFlags row={row.standard} comparator={comparator} /></Ratio></td>
    <GapCell row={row.standard} />
    <td className="measurement"><Ratio row={row.tuned} comparator={comparator}><LifecycleFlags row={row.tuned} comparator={comparator} /></Ratio></td>
    <GapCell row={row.tuned} />
    <td className="measurement"><Gain result={row.gain} label="Tuned ratio divided by default ratio" /></td>
  </tr>)}</tbody></table></div>;
}

type SpecializedVariant = { key: "regular" | "specialized" | "compiled"; suffix: string; label: string; short: string };
type SpecializedPattern = { id: string; anchor: Row; variants: Partial<Record<SpecializedVariant["key"], Row>>;
  gains: Partial<Record<SpecializedVariant["key"], Result>>; evidence: Array<{ label: string; row: Row }> };

// Column order; each page shows only the variants it measured.
const specializedVariants: SpecializedVariant[] = [
  { key: "regular", suffix: "/regular-engine", label: "Regular engine", short: "Regular" },
  { key: "specialized", suffix: "", label: "Specialized engine", short: "Specialized" },
  { key: "compiled", suffix: "/generated", label: "Compiled", short: "Compiled" },
];
const [, specializedEngine] = specializedVariants;

function specializedVariantOf(row: Row): SpecializedVariant {
  return specializedVariants.find(variant => variant.suffix && row.caseId.endsWith(variant.suffix)) ?? specializedEngine;
}

function measuredVariants(patterns: SpecializedPattern[]): SpecializedVariant[] {
  return specializedVariants.filter(variant => patterns.some(pattern => pattern.variants[variant.key]));
}

// Each later variant's gain over the page's first variant is computed once per pattern.
export function specializedPatternRows(rows: Row[]): SpecializedPattern[] {
  const patterns = rows.filter(row => row.population === "specialized-patterns");
  const anchorKey = (row: Row) => `${row.caseId.slice(0, row.caseId.length - specializedVariantOf(row).suffix.length)}/${row.operation}`;
  const byAnchor = new Map<string, SpecializedPattern>(patterns.filter(row => specializedVariantOf(row) === specializedEngine)
    .map(anchor => [anchorKey(anchor), { id: anchor.id, anchor, variants: { specialized: anchor }, gains: {}, evidence: [] }]));
  for (const row of patterns.filter(row => specializedVariantOf(row) !== specializedEngine)) {
    const pattern = byAnchor.get(anchorKey(row));
    if (!pattern) throw new Error(`Missing specialized-engine partner for ${row.id}`);
    pattern.variants[specializedVariantOf(row).key] = row;
  }
  const [baseline, ...later] = measuredVariants([...byAnchor.values()]);
  for (const pattern of byAnchor.values()) {
    pattern.evidence = specializedVariants.flatMap(variant => pattern.variants[variant.key] ? [{ label: variant.label, row: pattern.variants[variant.key]! }] : []);
    const baselineRow = pattern.variants[baseline.key];
    for (const variant of later) {
      const variantRow = pattern.variants[variant.key];
      if (!variantRow || !baselineRow) continue;
      // The importer pairs each host's two variant times; their warnings against the comparator do not apply.
      const warnings = variantRow.variantGainWarnings?.[baselineRow.id];
      if (!warnings) throw new Error(`Missing host-paired gain warnings for ${variantRow.id}`);
      pattern.gains[variant.key] = variantGain(variantRow.result, baselineRow.result, warnings);
    }
  }
  return [...byAnchor.values()];
}

function SpecializedPatternTable({ rows, like, comparator }: { rows: Row[]; like: boolean; comparator: string }) {
  const [sort, setSort] = useState<Sort>(null);
  const patterns = specializedPatternRows(rows);
  const present = measuredVariants(patterns);
  const [baseline, ...later] = present;
  const last = present[present.length - 1];
  const ordered = sorted(patterns, sort, (pattern, key) => ({
    name: `${pattern.anchor.name} ${pattern.anchor.operation}`, comparatorNs: pattern.anchor.result.comparatorNs, ratio: pattern.variants[last.key]?.result.ratio,
    gap: pattern.variants[last.key] && lossGap(pattern.variants[last.key]!)?.instructions,
    ...Object.fromEntries(present.map(variant => [`${variant.key}Ns`, pattern.variants[variant.key]?.result.candidateNs])),
    ...Object.fromEntries(later.map(variant => [`${variant.key}Gain`, pattern.gains[variant.key]?.ratio])),
  } as Record<string, SortValue>)[key]);
  return <div className="table-scroll"><table className="measurement-table specialized-pattern-table"><thead><tr>
    <Header label={like ? "Pattern" : "Pattern / operation"} column="name" sort={sort} setSort={setSort} />
    <Header label={comparator} column="comparatorNs" sort={sort} setSort={setSort} title={`${comparator}, measured once per host and shared by every Regulator variant`} />
    {present.map(variant => <Header key={variant.key} label={variant.label} column={`${variant.key}Ns`} sort={sort} setSort={setSort} />)}
    {later.map(variant => <Header key={variant.key} label={`${variant.short} vs ${baseline.short.toLowerCase()}`} column={`${variant.key}Gain`} sort={sort} setSort={setSort} />)}
    <Header label={`${last.short} vs ${comparator}`} column="ratio" sort={sort} setSort={setSort} />
    <Header label="Gap (instructions)" column="gap" sort={sort} setSort={setSort} title={`${last.label}: ${gapTitle}`} />
  </tr></thead><tbody>{ordered.map(pattern => <tr key={pattern.id}>
    <td><Name row={pattern.anchor} variants={pattern.evidence} comparator={comparator} hideOperation={like} /></td>
    <td className="measurement">{duration(pattern.anchor.result.comparatorNs)}<TimingFlags row={pattern.anchor} side="comparator" comparator={comparator} /></td>
    {present.map(variant => {
      const row = pattern.variants[variant.key];
      return <td key={variant.key} className="measurement">{row && <>{duration(row.result.candidateNs)}<TimingFlags row={row} side="candidate" comparator={comparator} /></>}</td>;
    })}
    {later.map(variant => <td key={variant.key} className="measurement"><Gain result={pattern.gains[variant.key]} label={`${variant.label} ratio divided by ${baseline.label.toLowerCase()} ratio`} /></td>)}
    <td className="measurement"><Ratio row={pattern.variants[last.key]} comparator={comparator} /></td>
    <GapCell row={pattern.variants[last.key]} />
  </tr>)}</tbody></table></div>;
}

function selectionFromUrl(data?: Pick<ReportData, "platforms" | "languages">): Selection {
  const parameters = new URLSearchParams(typeof window === "undefined" ? "" : window.location.hash.slice(1));
  const available = data ? cpuOrder(data) : cpuOrders.flat();
  const languages = data ? languageOrder(data) : languageOrders[0];
  return { language: languages.includes(parameters.get("language") || "") ? parameters.get("language")! : "re2",
    cpu: available.includes(parameters.get("cpu") || "") ? parameters.get("cpu")! : data ? available[0] : "c9g",
    memory: parameters.get("memory") === "safe" ? "safe" : "native" };
}

export function LanguageReport({ data, selection = selectionFromUrl(data), onSelect, loading, error, onRetry }: {
  data: ReportData; selection?: Selection; onSelect?: (selection: Selection) => void;
  loading?: boolean; error?: string | null; onRetry?: () => void;
}) {
  const select = (changes: Partial<Selection>) => {
    const next = { ...selection, ...changes };
    onSelect?.(next);
    window.location.hash = new URLSearchParams(next).toString();
  };
  const { language, cpu, memory } = selection;
  const comparator = data.languages[language].comparator;
  // Only the 1.0 preliminary snapshot mixed development revisions; a candidate build is one campaign.
  const mixed = data.publication?.sourcePolicy === "mixed-development-revisions";
  // Mixed preliminary tables show median estimates, but never assert a consistent winner when hosts
  // disagree. Full measurement warnings remain in row details; full gain warnings remain in downloads.
  const shownWarnings = (warnings: string[]) => mixed ? warnings.filter(warning => warning.includes("disagree")) : warnings;
  const selectedRows = data.rows.filter(row => row.language === language && row.platform === cpu && row.memoryMode === memory && workloadSection(row) !== "diagnostic-only")
    .map(row => ({ ...row, measurementWarnings: row.result.warnings, result: { ...displayResult(row.result), warnings: shownWarnings(row.result.warnings) },
      ...(row.variantGainWarnings ? { variantGainWarnings: Object.fromEntries(Object.entries(row.variantGainWarnings).map(([id, warnings]) => [id, shownWarnings(warnings)])) } : {}) }));
  const pending = selectedRows.filter(row => comparisonIssue(row));
  const rows = selectedRows.filter(row => !comparisonIssue(row));
  const ordinary = rows.filter(row => row.population === "ordinary-scalar" && row.operation.startsWith("reused"));
  const bulk = rows.filter(row => workloadSection(row) === "bulk-text");
  const literalProbes = rows.filter(row => workloadSection(row) === "literal-probe");
  const specialized = rows.filter(row => workloadSection(row) === "specialized-patterns");
  const columnRows: number[] = [...new Set(specialized.flatMap(row => row.workload?.columnRows ?? []))].sort((left, right) => left - right);
  const operations = rows.filter(row => row.population === "trino-operations");
  const diagnostic = rows.filter(row => workloadSection(row) === "diagnostics-and-stress")
    .map(row => row.population === "bulk-text" ? { ...row, family: "reported-regression" } : row);
  const stress = rows.filter(row => workloadSection(row) === "synthetic-stress");
  const lifecycle = rows;
  const hasLifecycle = lifecycle.some(row => row.operation.startsWith("singleUse"));
  const groupNames = [...new Set(diagnostic.map(row => row.family))].sort();
  const qualityRows = displayedMeasurements(rows);
  const section = (title: string, explanation: React.ReactNode, contents: React.ReactNode) => <section className="scan-section"><div className="scan-title"><h2>{title}</h2></div><p className="section-note">{explanation}</p>{contents}</section>;
  return <main className="scan-page language-page">
    <header className="scan-header"><div>{!data.sources.releaseVersion && <span className="snapshot-badge">{data.sources.currentLabel}</span>}
      <h1 className={data.sources.releaseVersion ? "release-title" : undefined}>Regulator{data.sources.releaseVersion && ` ${data.sources.releaseVersion}`} benchmarks</h1>
      <p className="source-line">{data.sources.releaseVersion
        ? ""
        : !mixed ? `Engine ${data.sources.currentCandidate.slice(0, 12)} · ` : ""}{data.sources.jdk}</p>
      {data.publication && <p className="source-line">{data.publication.note}</p>}</div>
      <div className="benchmark-controls"><div className="platform-switch">{cpuOrder(data).map(platform => <button key={platform} className={cpu === platform ? "active" : ""} onClick={() => select({ cpu: platform })}>{data.platforms[platform]}</button>)}</div>
        <label className="safe-toggle"><input type="checkbox" checked={memory === "safe"} onChange={event => select({ memory: event.target.checked ? "safe" : "native" })} /> Pure Java · no native memory</label>
      </div></header>
    <nav className="language-tabs" aria-label="Pattern API">{languageOrder(data).map(id => <button key={id} className={id === language ? "active" : ""} onClick={() => select({ language: id })}>{data.languages[id].label}</button>)}</nav>
    <div className="comparison-intro">
      <h2 className="comparison-heading">Regulator vs {comparator}</h2>
      {language === "re2" && <><p>Regulator receives the input bytes in Slice.</p><p>Native RE2 receives the same bytes.</p></>}
      {language === "java" && <><p>Regulator receives UTF-8 input in Slice.</p><p>JDK Pattern receives precomputed UTF-16 Java strings. Input conversion is outside the timings.</p></>}
      {language === "trino" && <><p>Regulator receives prepared UTF-8 input in Slice.</p><p>Trino's Joni library receives the same UTF-8 bytes.</p></>}
      {language === "rust" && <><p>Regulator receives the input bytes in Slice.</p><p>When the source turns Unicode off or the input is not valid UTF-8, the crate uses its bytes API and Regulator uses byte mode. Otherwise the crate receives the same bytes as str. UTF-8 validation is outside the timings.</p></>}
      {language === "like" && <><p>Regulator receives UTF-8 input and patterns in Slice.</p><p>Trino LikeMatcher receives UTF-8 input bytes and patterns as Java strings.</p></>}
    </div>
    <MeasurementQuality rows={qualityRows} cpu={cpu} comparator={comparator} platform={data.platforms[cpu]} memory={memory === "safe" ? "pure Java" : "native access"} releaseVersion={data.sources.releaseVersion} />
    {pending.length > 0 && <p className="section-note">{pending.length} measurements are separated below because they need remeasurement or use different API work. Their recorded times remain available, but they are not included in speed comparisons.</p>}
    {ordinary.length > 0 && section("Everyday regex operations", "Compiled patterns over rotating inputs. Contains and count are measured separately.", <ResultsTable rows={ordinary} comparator={comparator} />)}
    {specialized.length > 0 && section("Specialized engine and compiler", <>Patterns that get a scan plan run on the specialized engine, which interprets that plan{specialized.some(row => row.caseId.endsWith("/regular-engine")) && "; the regular engine runs the same patterns without one"}. The opt-in compiler, enabled with setGenerateBytecode, turns the plan into bytecode generated for each pattern. Times are warmed steady-state operations and exclude compilation.{columnRows.length > 0 && ` Each time is one scan of the first ${columnRows.map(count => count.toLocaleString("en-US")).join(" or ")} rows of a TPC-H column.`} Ratios between Regulator variants divide their {comparator}-relative ratios, so differences between hosts cancel.</>, <SpecializedPatternTable rows={specialized} like={language === "like"} comparator={comparator} />)}
    {hasLifecycle && section(language === "like" ? "LIKE pattern lifecycle" : "Regex pattern lifecycle", <>Single use includes compilation and the first operation. Multi-use measures each operation with a warmed compiled pattern.{language !== "like" && " Break-even is an estimate, not a measured execution count."}
      {language === "like" && <><br />Trino's DFA optimization applies only to some patterns. Those patterns have separate enabled and disabled rows.</>}</>, <LifecycleTable rows={lifecycle} like={language === "like"} comparator={comparator} />)}
    {operations.length > 0 && section("Trino regex operations", "Complete public operations using compiled patterns on rotating Slice values, compared with pinned Joni.", <ResultsTable rows={operations} comparator={comparator} />)}
    {bulk.length > 0 && section("Text processing workloads", "Document searches, tokenization, log parsing and capture workloads using compiled patterns. Expand a row for its pattern, operation and input details.", <ResultsTable rows={bulk} comparator={comparator} />)}
    {literalProbes.length > 0 && section("Literal probe distributions", <>Count every match of one literal across a whole corpus. Regulator runs once with its default uniform byte-frequency distribution and once with a distribution matched to the corpus; {comparator} has no such option. Tuned vs default divides the two {comparator}-relative ratios, so differences between hosts cancel.</>, <LiteralProbeTable rows={literalProbes} comparator={comparator} />)}
    {(diagnostic.length > 0 || stress.length > 0) && section("Adversarial and stress workloads", "Pathological inputs and synthetic stress tests. These are not representative of typical application performance; interpret them individually.", <div className="adversarial-groups">
      {groupNames.map(family => <details className="diagnostic-group" key={family}><summary>{family}<span>{diagnostic.filter(row => row.family === family).length} rows</span></summary><ResultsTable rows={diagnostic.filter(row => row.family === family)} comparator={diagnostic.find(row => row.family === family)?.comparator || comparator} /></details>)}
      {stress.length > 0 && <details className="diagnostic-group"><summary>Synthetic stress<span>{stress.length} rows</span></summary><ResultsTable rows={stress} comparator={comparator} /></details>}
    </div>)}
    {pending.length > 0 && section("Measurements awaiting alignment", "Historical evidence, not equivalent-work speed comparisons. The reason is shown beside each measurement. No timings have been corrected or estimated.", <ResultsTable rows={pending} comparator={comparator} />)}
    {loading ? <p role="status">Loading benchmark results…</p> : error ? <p role="alert">Unable to load results: {error} <button onClick={onRetry}>Retry</button></p> : selectedRows.length === 0 && <p>No measurements are available for this selection.</p>}
    <MeasurementNotes rows={qualityRows} />
    <footer className="scan-footer">
      {data.fullResultsUrl ? <a href={data.fullResultsUrl} download>Download report data (JSON.gz)</a> : <button type="button" onClick={() => {
        const url = URL.createObjectURL(new Blob([JSON.stringify(downloadData(data))], { type: "application/json" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "regulator-benchmark-results.json";
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }}>Download raw results (JSON)</button>}
    </footer>
  </main>;
}

declare global { interface Window { __REGULATOR_REPORT__?: ReportData } }

type SiteManifest = Omit<ReportData, "rows"> & { siteSchemaVersion: 1; pages: Record<string, string> };
export const selectionKey = ({ language, cpu, memory }: Selection) => `${language}-${cpu}-${memory}`;

// The manifest renders the loading view before any page arrives. Check the same
// display contract for both; full benchmark-evidence validation stays in tooling.
function validateDisplayMetadata(data: Omit<ReportData, "rows">) {
  const text = (value: unknown) => typeof value === "string" && value.trim().length > 0;
  if (!["currentLabel", "currentCandidate", "jdk"].every(key => text(data?.sources?.[key as keyof ReportData["sources"]]))
      || (data?.sources?.releaseVersion !== undefined && !text(data.sources.releaseVersion))
      || cpuOrder(data).length === 0 || Object.keys(data?.platforms || {}).length !== cpuOrder(data).length
      || (data?.sources?.releaseVersion === "1.0" && !cpuOrder(data).includes("r8g"))
      || !cpuOrder(data).every(cpu => text(data?.platforms?.[cpu]))
      || languageOrder(data).length === 0 || Object.keys(data?.languages || {}).length !== languageOrder(data).length
      || !languageOrder(data).every(language => text(data?.languages?.[language]?.label) && text(data?.languages?.[language]?.comparator))
      || (data?.publication !== undefined && !text(data.publication?.note))) {
    throw new Error("Invalid report display metadata");
  }
}

export function createReportLoader(request: typeof fetch) {
  let manifestPromise: Promise<SiteManifest> | undefined;
  const pages = new Map<string, Promise<ReportData>>();
  async function read(url: string, options?: RequestInit) {
    const response = await request(url, options);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  }
  return {
    manifest(): Promise<SiteManifest> {
      manifestPromise ??= read("./data/manifest.json", { cache: "no-cache" }).then((value: SiteManifest) => {
        validateDisplayMetadata(value);
        if (value.siteSchemaVersion !== 1 || value.schemaVersion !== 2 || !value.pages
            || !/^\.\/data\/[A-Za-z0-9._-]+\.json\.gz$/.test(value.fullResultsUrl || "")) throw new Error("Unsupported report manifest");
        return value;
      }).catch(error => { manifestPromise = undefined; throw error; });
      return manifestPromise;
    },
    page(selection: Selection, manifest: SiteManifest): Promise<ReportData> {
      const selectionId = selectionKey(selection);
      const filename = manifest.pages[selectionId];
      if (!filename || !/^[A-Za-z0-9._-]+\.json$/.test(filename)) return Promise.reject(new Error("Missing report selection"));
      const key = `${selectionId}/${filename}`;
      if (!pages.has(key)) {
        const requestedManifest = manifestPromise;
        pages.set(key, read(`./data/${filename}`).then((value: ReportData) => {
          validateDisplayMetadata(value);
          if (value.schemaVersion !== 2 || !value.selection || selectionKey(value.selection) !== selectionId
              || !Array.isArray(value.rows) || value.rows.some(row => row.language !== selection.language
                || row.platform !== selection.cpu || row.memoryMode !== selection.memory)) throw new Error("Incorrect report selection");
          return value;
        }).catch(error => {
          pages.delete(key);
          // A deployment may have removed this manifest's files. Let the next
          // attempt revalidate it, without evicting a newer in-flight manifest.
          if (manifestPromise === requestedManifest) manifestPromise = undefined;
          throw error;
        }));
      }
      return pages.get(key)!;
    },
  };
}

function App() {
  const embedded = window.__REGULATOR_REPORT__;
  const [selection, setSelection] = useState(() => selectionFromUrl(embedded));
  const [loader] = useState(() => createReportLoader(fetch));
  const [manifest, setManifest] = useState<SiteManifest | null>(null);
  const [loaded, setLoaded] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const onChange = () => setSelection(selectionFromUrl(embedded || manifest || undefined));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, [embedded, manifest]);
  useEffect(() => {
    if (embedded) return;
    let cancelled = false;
    setError(null);
    void loader.manifest().then(async manifest => {
      if (cancelled) return;
      setManifest(manifest);
      if (!Object.hasOwn(manifest.languages, selection.language)) {
        setSelection({ ...selection, language: "re2" });
        return;
      }
      if (!Object.hasOwn(manifest.platforms, selection.cpu)) {
        const order = cpuOrder(manifest);
        setSelection({ ...selection, cpu: order.find(cpu => cpu.slice(1) === selection.cpu.slice(1)) || order[0] });
        return;
      }
      const data = await loader.page(selection, manifest);
      if (!cancelled) setLoaded(data);
    }).catch(failure => { if (!cancelled) setError(String(failure)); });
    // Requests are cached across selections. A late response cannot replace the active view.
    return () => { cancelled = true; };
  }, [selection.language, selection.cpu, selection.memory, attempt, embedded, loader]);
  const active = embedded || (loaded?.selection && selectionKey(loaded.selection) === selectionKey(selection) ? loaded : null);
  const data = active || (manifest && { ...manifest, rows: [] });
  const retry = () => setAttempt(value => value + 1);
  if (!data) return <main className="loading-page">{error
    ? <p role="alert">Unable to load results: {error} <button onClick={retry}>Retry</button></p>
    : <p role="status">Loading benchmark results…</p>}</main>;
  return <LanguageReport data={data} selection={selection} onSelect={setSelection}
    loading={!active && !error} error={error} onRetry={retry} />;
}

const root = typeof document === "undefined" ? null : document.getElementById("root");
if (root) createRoot(root).render(<StrictMode><App /></StrictMode>);
